import {api} from "./client";

export type EntitySource = "rdf" | "wikibase" | "wikidata";
export type EntityKind = "manuscript" | "work" | "person" | "place";

export interface SourceCounts {
  rdf: number;
  wikibase: number;
  wikidata: number;
}

export interface TypeAggregate {
  total: number;
  by_source: SourceCounts;
}

export interface ResearchSummary {
  total_manuscripts: number;
  total_works: number;
  total_persons: number;
  total_places: number;
  persons_by_role: {scribe: number; owner: number; author: number};
  triples: number;
  // Added when the summary aggregates RDF + Wikibase + Wikidata. Optional so
  // the type stays compatible during rollout.
  by_type?: Record<EntityKind, TypeAggregate>;
  sources_available?: Record<EntitySource, boolean>;
}

/** @deprecated Use CoOccurrenceGraph instead — the server now returns
 *  a pre-aggregated graph rather than raw pairs. */
export interface CoOccurrencePair {
  ms: string;
  work1: string;
  work1_label: string;
  work2: string;
  work2_label: string;
}

export interface CoOccurrenceNode {
  id: string;
  label: string;
  degree: number;
}

export interface CoOccurrenceEdge {
  work1: string;
  work2: string;
  shared_ms_count: number;
  ms_list: string[];
}

export interface CoOccurrenceGraph {
  nodes: CoOccurrenceNode[];
  edges: CoOccurrenceEdge[];
}

export interface NetworkNode {
  id: string;
  label: string;
  role: "scribe" | "author" | "owner";
  ms_count: number;
  /** Pre-computed spring-layout position (server-side networkx). */
  x: number;
  /** Pre-computed spring-layout position (server-side networkx). */
  y: number;
}

export interface NetworkLink {
  source: string;
  target: string;
  ms: string;
}

export interface PeopleNetwork {
  nodes: NetworkNode[];
  links: NetworkLink[];
}

export interface OwnerChain {
  ms: string;
  ms_label: string;
  owners: {name: string; uri: string}[];
}

/** @deprecated The server now returns PlaceRow (grouped). */
export interface GeoPoint {
  ms: string;
  ms_label: string;
  place: string;
  place_label: string;
  lat: number | null;
  lon: number | null;
  type: "production" | "mentioned";
}

export interface PlaceRow {
  place: string;
  place_label: string;
  lat: number | null;
  lon: number | null;
  type: "production" | "mentioned";
  ms_count: number;
  ms_labels: string[];
}

export interface HeatmapPoint {
  place: string;
  place_label: string;
  lat: number;
  lon: number;
  weight: number;
  type: "production" | "mentioned" | "both";
}

export interface ProvenanceEvent {
  type: "production" | "ownership" | "current_holder";
  label: string;
  uri: string | null;
  year: number | null;
  year_earliest: number | null;
  year_latest: number | null;
  place: string | null;
  owner_birth?: number | null;
  owner_death?: number | null;
}

export interface ProvenanceTimeline {
  ms: string;
  ms_label: string | null;
  events: ProvenanceEvent[];
}

export interface ManuscriptPick {
  control_number: string;
  label: string | null;
  production_year: number | null;
}

export type MapStopKind =
  | "production"
  | "owner"
  | "significant_place"
  | "acquisition"
  | "conservation"
  | "exhibition"
  | "current_holder";

export interface MapStop {
  kind: MapStopKind;
  label: string;
  uri?: string | null;
  lat: number | null;
  lon: number | null;
  year?: number | null;
  year_earliest?: number | null;
  year_latest?: number | null;
  birth_year?: number | null;
  death_year?: number | null;
  certain: boolean;
  inferred_geo: boolean;
  geo_source?: string | null;
  geo_source_label?: string | null;
  approved?: boolean | null;
  is_present?: boolean;
  time: number | null;
  has_point: boolean;
}

export interface MapEdge {
  from: number;
  to: number;
  inferred: boolean;
  directed: boolean;
}

export interface ProvenanceMap {
  control_number: string;
  ms_label: string | null;
  stops: MapStop[];
  edges: MapEdge[];
  dropped: {label: string; reason: string}[];
}

// ── Corpus Movement (Phase 2) ────────────────────────────────────────────

export interface CorpusManuscript {
  control_number: string;
  label: string;
  production_lat: number | null;
  production_lon: number | null;
  production_year: number | null;
  production_year_earliest: number | null;
  production_year_latest: number | null;
  production_place: string | null;
  has_production_point: boolean;
  holder_lat: number | null;
  holder_lon: number | null;
  holder_label: string;
  holder_place?: string | null;
  genres: string[];
  owners: string[];
  places: string[];
  event_places?: CorpusEventPlace[];
}

export interface CorpusEventPlace {
  type: string;
  place: string;
  lat: number | null;
  lon: number | null;
  year: number | null;
}

export interface YearCount {
  year: number;
  count: number;
}

export interface CorpusMovement {
  manuscripts: CorpusManuscript[];
  year_counts: YearCount[];
}

export interface CorpusMovementFacets {
  year_min: number | null;
  year_max: number | null;
  places: string[];
  genres: string[];
  owners: string[];
  holders?: string[];
}

export interface CorpusMovementFilters {
  from_year?: number;
  to_year?: number;
  place?: string;
  genre?: string;
  owner?: string;
  holder?: string;
}

export interface NeighborNode {
  uri: string;
  label: string | null;
  type: string;
  edge_type: string;
}

export interface PathNode {
  uri: string;
  label: string | null;
  type: string;
}

export interface PathEdge {
  source: string;
  target: string;
  label: string;
}

export interface PathResult {
  path: PathNode[];
  edges: PathEdge[];
}

const base = (projectId: string) => `/projects/${projectId}/research`;

const summaryInflight = new Map<string, Promise<ResearchSummary>>();

export const researchApi = {
  summary: (projectId: string) => {
    const existing = summaryInflight.get(projectId);
    if (existing) return existing;
    const req = api.get<ResearchSummary>(`${base(projectId)}/summary`).finally(() => {
      summaryInflight.delete(projectId);
    });
    summaryInflight.set(projectId, req);
    return req;
  },
  coOccurrence:   (projectId: string) => api.get<CoOccurrenceGraph>(`${base(projectId)}/co-occurrence`),
  peopleNetwork:  (projectId: string) => api.get<PeopleNetwork>(`${base(projectId)}/people-network`),
  ownership:      (projectId: string) => api.get<OwnerChain[]>(`${base(projectId)}/ownership`),
  geography:      (projectId: string) => api.get<PlaceRow[]>(`${base(projectId)}/geography`),
  geographyHeatmap: (projectId: string) => api.get<HeatmapPoint[]>(`${base(projectId)}/geography?mode=heatmap`),
  provenanceTimeline: (projectId: string, msUri: string, overlay?: string) =>
    api.get<ProvenanceTimeline>(
      `${base(projectId)}/provenance?ms=${encodeURIComponent(msUri)}${overlay ? `&overlay=${overlay}` : ""}`,
    ),
  manuscripts: (projectId: string) =>
    api.get<ManuscriptPick[]>(`${base(projectId)}/manuscripts`),
  provenanceMap: (projectId: string, cn: string, includeUnapproved = false) =>
    api.get<ProvenanceMap>(
      `${base(projectId)}/provenance-map?cn=${encodeURIComponent(cn)}${
        includeUnapproved ? "&include_unapproved=true" : ""
      }`,
    ),
  movementFacets: (projectId: string) =>
    api.get<CorpusMovementFacets>(`${base(projectId)}/movement/facets`),
  movement: (projectId: string, filters: CorpusMovementFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.from_year != null) params.set("from_year", String(filters.from_year));
    if (filters.to_year != null)   params.set("to_year",   String(filters.to_year));
    if (filters.place)             params.set("place",     filters.place);
    if (filters.genre)             params.set("genre",     filters.genre);
    if (filters.owner)             params.set("owner",     filters.owner);
    if (filters.holder)            params.set("holder",    filters.holder);
    const qs = params.toString();
    return api.get<CorpusMovement>(`${base(projectId)}/movement${qs ? `?${qs}` : ""}`);
  },
  neighbors: (projectId: string, uri: string) =>
    api.get<NeighborNode[]>(`${base(projectId)}/neighbors?uri=${encodeURIComponent(uri)}`),
  shortestPath: (projectId: string, fromUri: string, toUri: string) =>
    api.get<PathResult>(
      `${base(projectId)}/path?from=${encodeURIComponent(fromUri)}&to=${encodeURIComponent(toUri)}`,
    ),
};
